import { AxiosResponse } from 'axios';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  CloneWorkOrderDto,
  Configuration,
  CreateWorkOrderDto,
  CreateWorkOrderNoteDto,
  DocumentsApi,
  DownloadDocumentDTO,
  UpdateWorkOrderDto,
  WorkOrderExportOptionsDto,
  WorkOrdersApi,
  WorkOrderSearchParamsDto,
} from '@inosio/payments-sdk';
import { IpaUserExtended, selectCurrentUser } from '../../../shared/sagas/auth/authSlice';
import {
  getAddWorkOrderNote,
  getDeleteWorkOrderNote,
  getDownloadWorkOrderFile,
  getExportWorkOrder,
  getWorkOrderBase64Images,
  createWorkOrder,
  getWorkOrderVideos,
  getUpdateWorkOrderDetails,
  getWorkOrders,
  setAddWorkOrderNotes,
  setDeleteWorkOrderNote,
  setDownloadWorkOrderFile,
  setDownloadWorkOrderFileError,
  setErrorAddWorkOrderNotes,
  setErrorDeleteWorkOrderNote,
  setErrorExportWorkOrder,
  setExportWorkOrder,
  setWorkOrderBase64Images,
  setWorkOrderBase64ImagesError,
  setCreateWorkOrder,
  setCreateWorkOrderError,
  setWorkOrderVideos,
  setWorkOrderVideosError,
  setUpdateWorkOrderDetails,
  setUpdateWorkOrderDetailsError,
  setWorkOrders,
  setWorkOrdersFetchError,
  setWorkOrder,
  setWorkOrderError,
  getWorkOrderByUuid,
  getWorkOrderById,
  getSendWorkOrderEmail,
  setSendWorkOrderEmail,
  setSendWorkOrderEmailError,
} from './workorderSlice';
import { Stream } from 'stream';

const { REACT_APP_IPA_BASE_PATH } = process.env;

export function requestWorkOrders(accessToken: string, workOrderSearchParamsDto: WorkOrderSearchParamsDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.searchWorkOrders(workOrderSearchParamsDto);
}

export function requestCreateWorkOrder(accessToken: string, createWorkOrderDto: CreateWorkOrderDto | CloneWorkOrderDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.createWorkOrder(createWorkOrderDto);
}

export function requestMaintenanceImagesBase64(accessToken: string, remoteuuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaDocumentsApi = new DocumentsApi(apiConfig);
  return ipaDocumentsApi.getDocumentFile(remoteuuid, 'Base64');
}

export function requestWorkOrderVideos(accessToken: string, remoteuuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaDocumentsApi = new DocumentsApi(apiConfig);
  return ipaDocumentsApi.getTempUrl(remoteuuid);
}

export function requestDownloadWorkOrderFile(accessToken: string, remoteuuid: string, fileType: string, viewInBrowser?: boolean) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaDocumentsApi = new DocumentsApi(apiConfig);
  const responseType = 'arraybuffer';
  const contentDisposition = fileType;
  const headerParams = {
    'Content-Disposition': contentDisposition,
  };
  return ipaDocumentsApi.getDocumentFile(remoteuuid, undefined, viewInBrowser, { responseType, headers: headerParams });
}

export function requestUpdateWorkOrderDetails(accessToken: string, wo_uuid: string, updateWorkOrderDto: UpdateWorkOrderDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.updateWorkOrder(wo_uuid, updateWorkOrderDto);
}

export function requestExportWorkOrder(accessToken: string, wo_uuid: string, workOrderExportOptionsDto: WorkOrderExportOptionsDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  const responseType = 'arraybuffer';
  const contentDisposition = 'application/pdf';
  const headerParams = {
    'Content-Disposition': contentDisposition,
  };
  return ipaWorkOrdersApi.exportWorkOrder(wo_uuid, workOrderExportOptionsDto, { responseType, headers: headerParams });
}

export function requestAddWorkOrderNote(accessToken: string, wo_uuid: string, createWorkOrderNoteDto: CreateWorkOrderNoteDto) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.addWorkOrderNote(wo_uuid, createWorkOrderNoteDto);
}

export function requestDeleteWorkOrderNote(accessToken: string, wo_uuid: string, workOrderNoteId: number) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.deleteWorkOrderNote(wo_uuid, workOrderNoteId);
}

export function requestGetWorkOrderByUuid(accessToken: string, wo_uuid: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.findWorkOrderByUuid(wo_uuid);
}

export function requestGetWorkOrderById(accessToken: string, workorderId: string) {
  const apiConfig = new Configuration({
    basePath: REACT_APP_IPA_BASE_PATH,
    accessToken,
  });
  const ipaWorkOrdersApi = new WorkOrdersApi(apiConfig);
  return ipaWorkOrdersApi.getWorkOrderById(workorderId);
}

export function* handleFetchWorkOrders(action: { type: typeof getWorkOrders; payload: WorkOrderSearchParamsDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestWorkOrders, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setWorkOrders(data));
  } catch (error: any) {
    yield put(
      setWorkOrdersFetchError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleCreateWorkOrder(action: { type: typeof createWorkOrder; payload: CreateWorkOrderDto | CloneWorkOrderDto }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestCreateWorkOrder, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setCreateWorkOrder(data));
  } catch (error: any) {
    yield put(
      setCreateWorkOrderError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleFetchMaintenanceImageBase64(action: { type: typeof getWorkOrderBase64Images; payload: string }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<DownloadDocumentDTO> = yield call(requestMaintenanceImagesBase64, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setWorkOrderBase64Images(data));
  } catch (error: any) {
    yield put(
      setWorkOrderBase64ImagesError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleFetchWorkOrderVideos(action: { type: typeof getWorkOrderVideos; payload: string }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestWorkOrderVideos, currentUser.accessToken, action.payload);
    const { data } = response;
    yield put(setWorkOrderVideos(data));
  } catch (error: any) {
    yield put(
      setWorkOrderVideosError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleUpdateWorkOrderDetails(action: { type: typeof getUpdateWorkOrderDetails; payload: { woUuid: string; updateWoDto: UpdateWorkOrderDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestUpdateWorkOrderDetails, currentUser.accessToken, action.payload.woUuid, action.payload.updateWoDto);
    const { data } = response;
    yield put(setUpdateWorkOrderDetails(data));
  } catch (error: any) {
    yield put(
      setUpdateWorkOrderDetailsError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleSendWorkOrderEmail(action: { type: typeof getUpdateWorkOrderDetails; payload: { woUuid: string; updateWoDto: UpdateWorkOrderDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestUpdateWorkOrderDetails, currentUser.accessToken, action.payload.woUuid, action.payload.updateWoDto);
    const { data } = response;
    yield put(setSendWorkOrderEmail(data));
  } catch (error: any) {
    yield put(
      setSendWorkOrderEmailError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleDownloadWorkOrderFiles(action: { type: typeof getDownloadWorkOrderFile; payload: { remoteUuid: string; fileType: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<Stream> = yield call(requestDownloadWorkOrderFile, currentUser.accessToken, action.payload.remoteUuid, action.payload.fileType);

    // download stream
    let fileName = 'attachment'; // fallback
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers['content-disposition']);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
    const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: response.headers['content-type'] }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);

    yield put(setDownloadWorkOrderFile());
  } catch (error: any) {
    yield put(
      setDownloadWorkOrderFileError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleAddWorkOrderNote(action: { type: typeof getAddWorkOrderNote; payload: { woUuid: string; createWorkOrderNoteDto: CreateWorkOrderNoteDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestAddWorkOrderNote, currentUser.accessToken, action.payload.woUuid, action.payload.createWorkOrderNoteDto);
    const { data } = response;
    yield put(setAddWorkOrderNotes(data));
  } catch (error: any) {
    yield put(
      setErrorAddWorkOrderNotes({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleDeleteWorkOrderNote(action: { type: typeof getDeleteWorkOrderNote; payload: { woUuid: string; workOrderNoteId: number } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestDeleteWorkOrderNote, currentUser.accessToken, action.payload.woUuid, action.payload.workOrderNoteId);
    const { data } = response;
    yield put(setDeleteWorkOrderNote(data));
  } catch (error: any) {
    yield put(
      setErrorDeleteWorkOrderNote({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleExportWorkOrder(action: { type: typeof getExportWorkOrder; payload: { woUuid: string; workOrderExportOptionsDto: WorkOrderExportOptionsDto } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestExportWorkOrder, currentUser.accessToken, action.payload.woUuid, action.payload.workOrderExportOptionsDto);

    // download stream
    let fileName = 'attachment'; // fallback
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(response.headers['content-disposition']);
    if (matches != null && matches[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }
    const url = window.URL.createObjectURL(new Blob([response.data as unknown as BlobPart], { type: response.headers['content-type'] }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}`);
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);

    yield put(setExportWorkOrder());
  } catch (error: any) {
    yield put(
      setErrorExportWorkOrder({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleGetWorkOrderByUuid(action: { type: typeof getExportWorkOrder; payload: { woUuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestGetWorkOrderByUuid, currentUser.accessToken, action.payload.woUuid);
    const { data } = response;
    yield put(setWorkOrder(data));
  } catch (error: any) {
    yield put(
      setWorkOrderError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleGetWorkOrderById(action: { type: typeof getExportWorkOrder; payload: { workOrderId: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestGetWorkOrderById, currentUser.accessToken, action.payload.workOrderId);
    const { data } = response;
    yield put(setWorkOrder(data));
  } catch (error: any) {
    yield put(
      setWorkOrderError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* workOrderSaga() {
  yield takeLatest(getWorkOrders.type, handleFetchWorkOrders);
  yield takeLatest(getWorkOrderByUuid.type, handleGetWorkOrderByUuid);
  yield takeLatest(getWorkOrderById.type, handleGetWorkOrderById);
  yield takeLatest(createWorkOrder.type, handleCreateWorkOrder);
  yield takeEvery(getWorkOrderBase64Images.type, handleFetchMaintenanceImageBase64);
  yield takeEvery(getWorkOrderVideos.type, handleFetchWorkOrderVideos);
  yield takeLatest(getUpdateWorkOrderDetails.type, handleUpdateWorkOrderDetails);
  yield takeLatest(getSendWorkOrderEmail.type, handleSendWorkOrderEmail);
  yield takeEvery(getDownloadWorkOrderFile.type, handleDownloadWorkOrderFiles);
  yield takeLatest(getAddWorkOrderNote.type, handleAddWorkOrderNote);
  yield takeLatest(getDeleteWorkOrderNote.type, handleDeleteWorkOrderNote);
  yield takeLatest(getExportWorkOrder.type, handleExportWorkOrder);
}
